/**
 * Authoritative session duration (seconds) to persist when a recording stops
 * (APP-7623).
 *
 * The desktop stop flow historically PATCHed `session_duration` once, at the
 * click site, from the UI timer alone. If that PATCH failed (network blip,
 * token expiry, app closed right after stop) — or the timer had been wiped
 * (window reload mid-session; resume seeded from a stale `duration`) — nothing
 * ever re-patched, and the backend blocks note generation on `duration=0` even
 * though the chunks uploaded fine (Rust retries those aggressively). Same root
 * cause as Android APP-3978 / iOS APP-3301.
 *
 * This resolves the best-known duration from four independent sources:
 * - `timerSeconds` — the UI timer, which accumulates across pause/resume
 *   segments on the same session (APP-6013), but can be wiped by a reload.
 * - `cachedDurationSeconds` — the last server-confirmed / optimistically
 *   patched value, so a retry can never move a good value backwards.
 * - `t11nDurationSeconds` — the summed duration of the session's uploaded
 *   transcription-service chunks (server-confirmed, accumulated across ALL
 *   recordings). Recovers the multi-segment total when the timer is gone and
 *   the cache is stale — the same fallback `useUpdateTimerFromSession` uses.
 * - `rustDurationMs` — the Rust upload pipeline's wall-clock for the segment
 *   that just stopped (`StopOnlineChunkUploadResult.durationMs`). Authoritative
 *   even when the UI timer is gone, but covers only the latest segment.
 *
 * `Math.max` across the sources is safe because a session's duration only ever
 * grows: the timer starts from the session's existing duration and counts up.
 * Callers must skip the PATCH when this returns 0 — a 0 write carries no
 * information and could clobber a good server value.
 */
export function resolveStopDurationSeconds({
  timerSeconds,
  cachedDurationSeconds,
  t11nDurationSeconds,
  rustDurationMs,
}: {
  timerSeconds: number;
  cachedDurationSeconds?: number | null;
  t11nDurationSeconds?: number | null;
  rustDurationMs?: number | null;
}): number {
  const safeSeconds = (value: number | null | undefined): number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

  return Math.max(
    safeSeconds(timerSeconds),
    safeSeconds(cachedDurationSeconds),
    safeSeconds(t11nDurationSeconds),
    safeSeconds(Math.round((rustDurationMs ?? 0) / 1000))
  );
}
